import React from "react";
import { Check, MapPin, ChevronRight, Search, Home, School, Users, Compass } from "lucide-react";
import { cn } from "../../lib/utils";
import { useLocationContext, LocationWithHierarchy } from "../../client/contexts/LocationContext";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";

interface LocationSelectorMobileProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabCategory = "all" | "res" | "academic" | "campus";

export default function LocationSelectorMobile({
  isOpen,
  onClose,
}: LocationSelectorMobileProps) {
  const { locations, selectedLocation, setSelectedLocation } = useLocationContext();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [activeTab, setActiveTab] = React.useState<TabCategory>("all");
  const navigate = useNavigate();

  const filteredByTab = React.useMemo(() => {
    if (activeTab === "all") return locations;
    return locations.filter((l: any) => {
      const type = l.locationType?.slug;
      if (activeTab === "res") return type === "RESIDENCE" || type === "DORM";
      if (activeTab === "academic") return type === "FACULTY";
      if (activeTab === "campus")
        return ["STUDENT_CENTER", "LIBRARY", "SPORTS_COMPLEX", "FOOD_COURT"].includes(type || "");
      return true;
    });
  }, [locations, activeTab]);

  const filteredLocations = filteredByTab.filter((loc) =>
    ((loc as any).name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (location: any) => {
    setSelectedLocation(location);
    onClose();
    
    // Navigate to the selected location's appropriate page
    const currentPath = window.location.pathname;

    if (location.locationCode) {
      // If we're on a public page or explicitly set dashboard, go to location dashboard
      if (currentPath === '/' || currentPath === '/dashboard' || !currentPath.includes('/locations/')) {
        navigate(`/locations/${location.locationCode}/dashboard`);
        return;
      }
      
      // If we're on an admin page, stay there but context updates
      if (currentPath.startsWith("/admin")) {
        return;
      }
      
      // Preserve current module context when switching between locations
      if (currentPath.includes("/notices")) {
        navigate(`/locations/${location.locationCode}/notices`);
      } else if (currentPath.includes("/tenants")) {
        navigate(`/locations/${location.locationCode}/tenants`);
      } else if (currentPath.includes("/events")) {
        navigate(`/locations/${location.locationCode}/events`);
      } else if (currentPath.includes("/facilities")) {
        navigate(`/locations/${location.locationCode}/facilities`);
      } else if (currentPath.includes("/buy-sell")) {
        navigate(`/locations/${location.locationCode}/buy-sell`);
      } else if (currentPath.includes("/food-court")) {
        navigate(`/locations/${location.locationCode}/food-court`);
      } else if (currentPath.includes("/lost-found")) {
        navigate(`/locations/${location.locationCode}/lost-found`);
      } else if (currentPath.includes("/retail")) {
        navigate(`/locations/${location.locationCode}/retail`);
      } else if (currentPath.includes("/services")) {
        navigate(`/locations/${location.locationCode}/services`);
      } else if (currentPath.includes("/accommodation")) {
        navigate(`/locations/${location.locationCode}/accommodation`);
      } else if (currentPath.includes("/carpooling")) {
        navigate(`/locations/${location.locationCode}/carpooling`);
      } else {
        navigate(`/locations/${location.locationCode}/dashboard`);
      }
    } else {
      navigate("/dashboard");
    }
  };

  const tabs = [
    { id: "all", label: "All", icon: Compass },
    { id: "res", label: "Residences", icon: Home },
    { id: "academic", label: "Academic", icon: School },
    { id: "campus", label: "Campus Life", icon: Users },
  ];

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[80vh] p-0 flex flex-col rounded-t-2xl bg-background">
        <SheetHeader className="p-4 pb-2 border-b bg-background/80 backdrop-blur-md sticky top-0 z-10 rounded-t-2xl">
          <SheetTitle className="text-left flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Select Location
          </SheetTitle>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-muted border-none rounded-lg focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
          
          {/* Category Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar mt-4 -mx-1 pb-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabCategory)}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full transition-all whitespace-nowrap border",
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-muted/50 text-muted-foreground border-transparent hover:bg-muted"
                )}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto p-2">
          {filteredLocations.map((loc: any) => {
            const isSelected = selectedLocation?.id === loc.id;
            return (
              <button
                key={loc.id}
                onClick={() => handleSelect(loc)}
                className={cn(
                  "w-full flex items-center justify-between p-4 rounded-xl mb-1 transition-colors",
                  isSelected ? "bg-primary/10 text-primary border border-primary/20" : "hover:bg-muted active:bg-muted/80 border border-transparent"
                )}
              >
                <div className="flex flex-col items-start">
                  <span className="font-semibold text-base">{(loc as any).name}</span>
                  {(loc as any).parentLocation && (
                    <span className="text-xs opacity-70">{(loc as any).parentLocation.name}</span>
                  )}
                </div>
                {isSelected ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
            );
          })}
          
          {filteredLocations.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No locations found in this category
            </div>
          )}
        </div>
        
        <div className="p-4 bg-muted/30 border-t pb-8">
            <p className="text-xs text-center text-muted-foreground">
                Selecting a location will update your view.
            </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
